<template>
  <div>
    <PageHeader title="Sinistres" />
    <SearchToolbar
      ref="searchToolbar"
      class="mb-4"
      :placeholder="$t('SearchClaims')"
      :initial-keywords="keywords"
      @update-keyword-search="updateKeywordSearch"
    />
    <Tabs :tabs="tabs" @tab-selected="selectTab" />
    <PaginatedClaimList
      v-if="claims?.length > 0"
      :total-count="totalCount"
      :total-pages="totalPages"
      :current-page="currentPage"
      :claims="claims"
      @changePage="handleChangePage"
      @redirectToClaimDetailsPage="redirectToClaimDetailsPage"
    />
    <div v-else class="text-center mt-4 py-2">
      <p>Aucun sinistre</p>
    </div>
  </div>
</template>

<script>
import PaginatedClaimList from "@/components/claim/list/PaginatedClaimList.vue";
import { mapState, mapActions, mapGetters } from "vuex";
import { PageHeader, SearchToolbar, Tabs } from "@estaly/ui";
import { setQueryParams } from "@estaly/ui/src/utils/query_params";

export default {
  components: {
    PageHeader,
    SearchToolbar,
    PaginatedClaimList,
    Tabs,
  },

  data() {
    return {
      currentPage: 1,
      availableFilters: {
        status: [],
        claimHandler: [],
      },
      selectedFilters: {
        status: [],
        claimHandler: [],
      },
      keywords: null,
      currentTab: "action-required",
    };
  },

  computed: {
    ...mapState("claims", { claims: "index", totalCount: "totalCount", totalPages: "totalPages" }),
    ...mapGetters("claims", ["claimHandlers", "statuses"]),
    ...mapGetters("store", ["selectedStore"]),

    firstPartUrl() {
      return this.$route.path.split("/").filter(Boolean)[0];
    },

    tabs() {
      return [
        {
          name: "action-required",
          displayName: "Action requise",
          current: this.currentTab === "action-required",
        },
        {
          name: "all",
          displayName: "Tous",
          current: this.currentTab === "all",
        },
      ];
    },
  },

  methods: {
    ...mapActions("claims", { fetchClaims: "fetchIndex" }),
    ...mapActions("store", { withLoader: "withLoader" }),

    handleChangePage(page) {
      this.updatePage(page);
    },

    redirectToClaimDetailsPage(claimId) {
      this.$router.push(`/${this.firstPartUrl}/claims/${claimId}`);
    },

    async updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.currentPage = 1;
      await this.retrieveClaims();
      await this.updatePage(1);
    },

    async updatePage(newPage, refreshClaims = true) {
      this.currentPage = newPage;

      if (refreshClaims) {
        setQueryParams({ page: newPage });
        await this.retrieveClaims();
      }
    },

    async retrieveClaims() {
      await this.withLoader(async () => {
        await this.fetchClaims({
          page: this.currentPage,
          keywords: this.keywords,
          includes: ["customer", "claim_product", "store"],
          stores: this.selectedStore ? [this.selectedStore.salesChannel] : [],
          ...this.selectedFilters,
        });
      });
    },

    async applyTabFilters(tabName) {
      this.currentPage = 1;

      if (tabName === "action-required") {
        this.selectedFilters.status = ["waiting_for_quotation", "waiting_for_invoice"];
        this.selectedFilters.claimHandler = ["merchant"];
        await this.updatePage(1);
      } else if (tabName === "all") {
        this.selectedFilters.status = [];
        this.selectedFilters.claimHandler = [];
        await this.updatePage(1);
      }
    },

    async selectTab(tab) {
      this.currentTab = tab.name;
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, tab: tab.name },
      });
      await this.applyTabFilters(tab.name);
    },

    focusSearchInput() {
      const input = this.$refs.searchToolbar?.$el?.querySelector("input");
      if (input) input.focus();
    },
  },

  async mounted() {
    this.availableFilters.claimHandler = this.claimHandlers;
    this.availableFilters.status = this.statuses;
    this.currentTab = this.$route.query.tab || "action-required";
    await this.applyTabFilters(this.currentTab);
  },

  watch: {
    "$route.query.focusSearch": {
      handler(shouldFocus) {
        if (shouldFocus === "true") {
          this.$nextTick(() => {
            this.focusSearchInput();
            this.$router.replace({
              query: { ...this.$route.query, focusSearch: undefined },
            });
          });
        }
      },
      immediate: true,
    },

    "$route.query.tab": {
      async handler(newTab) {
        if (newTab && this.currentTab !== newTab) {
          this.currentTab = newTab;
          await this.applyTabFilters(newTab);
        }
      },
    },
  },
};
</script>
